import { useEffect, useState } from "react";

/**
 * Establish state for an input control with debounced updates.
 * @param delay Time to wait before activating newly entered text.
 * @param initial Initial value.
 * @param active Active (already debounced) value.
 * @param setActive Callback to set the active value.
 * @returns Current raw input value, a function to apply the input value from onChange, and a function to cancel the debouncer.
 */
export function useInputDebouncer<T>(delay: number, initial: T, active: T, setActive: (arg: T) => void) {
    const [input, setInput] = useState(initial);
    const debouncer = useInputDebouncerCore(active, delay, setActive, setInput);

    useEffect(() => {
        return () => debouncer.cancel();
    }, [debouncer]);

    return [input, debouncer.applyFromInput.bind(debouncer), debouncer.cancel.bind(debouncer)] as const;
}

function useInputDebouncerCore<T>(active: T, ...debouncerArgs: ConstructorParameters<typeof Debouncer<T>>) {
    const [debouncer] = useState(() => new Debouncer(...debouncerArgs));
    debouncer.update(debouncerArgs);

    const [prevActive, setPrevActive] = useState(active);
    if (active !== prevActive) {
        setPrevActive(active);
        debouncer.applyFromActive(active);
    }

    return debouncer;
}

class Debouncer<T> {
    private timeout: NodeJS.Timeout | undefined = undefined;

    constructor(
        private readonly delay: number,
        private setActive: (arg: T) => void,
        private readonly setInput: (arg: T) => void,
    ) {}

    update(debouncerArgs: ConstructorParameters<typeof Debouncer<T>>) {
        this.setActive = debouncerArgs[1]; // update what can be updated, and ignore rest
    }

    applyFromInput(arg: T) {
        this.applyFromActive(arg);
        this.timeout = setTimeout(() => this.setActive(arg), this.delay);
    }

    applyFromActive(arg: T) {
        this.cancel();
        this.setInput(arg);
    }

    cancel() {
        clearTimeout(this.timeout);
        this.timeout = undefined;
    }
}
